import { ToastContext } from "context/ToastContext";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { useContext } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useGetMyTrainingQuery } from "store/queries/training";
import {
  useRequestOwnPasswordResetMutation,
  useUpdateOwnProfileMutation,
} from "store/queries/users";
import { onUserAccountLoad, userSelector } from "store/slices/user";
import styled from "styled-components";
import { CertifiedChip } from "training/components/CertifiedChip";

const PageContainer = styled.div`
  max-width: 640px;
  margin: 0 auto;
`;

const Card = styled.div`
  margin-bottom: 24px;
`;

const Title = styled.h2`
  margin: 0 0 4px;
`;

const SectionTitle = styled.h3`
  margin: 0 0 16px;
`;

const HelperText = styled.small`
  display: block;
  margin-top: 4px;
  color: var(--color-black-4);
`;

const Subtitle = styled.p`
  margin: 0 0 24px;
  color: var(--color-black-4);
`;

interface IProfileForm {
  name: string;
}

// Pulls a human-readable message out of an RTK Query error (backend returns { message } or,
// for the rate limiter, { error }; validation errors come back as a string[]).
function getErrorMessage(error: unknown, fallback: string): string {
  const data = (
    error as { data?: { message?: string | string[]; error?: string } }
  )?.data;
  const message = data?.message ?? data?.error;
  if (Array.isArray(message)) return message.join(" ");
  return message || fallback;
}

export function MyAccount() {
  const dispatch = useDispatch();
  const { current: toast } = useContext(ToastContext);
  const userState = useSelector(userSelector);
  // CLE-3015 — the "Clear Certified" chip beside the name, only while the account's flag is on.
  const { data: training } = useGetMyTrainingQuery(userState.currentAccount);
  const certifiedAt = training?.data?.enabled
    ? training.data.clearCertifiedAt
    : null;

  const [updateOwnProfile, { isLoading: isSavingProfile }] =
    useUpdateOwnProfileMutation();
  const [requestOwnPasswordReset, { isLoading: isSendingReset }] =
    useRequestOwnPasswordResetMutation();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isValid },
  } = useForm<IProfileForm>({
    mode: "onChange",
    defaultValues: { name: userState.name || "" },
  });

  const onSaveProfile = handleSubmit(async ({ name }) => {
    const trimmed = name.trim();
    try {
      await updateOwnProfile({ name: trimmed }).unwrap();
      // Keep the rest of the user slice intact; only the name changed. This refreshes the
      // name shown in the top-right user menu without a reload.
      dispatch(onUserAccountLoad({ ...userState, name: trimmed }));
      reset({ name: trimmed });
      toast?.show({
        severity: "success",
        summary: "Profile updated",
        detail: "Your name has been updated.",
      });
    } catch (error) {
      toast?.show({
        severity: "error",
        summary: "Update failed",
        detail: getErrorMessage(error, "Could not update your profile."),
      });
    }
  });

  const onRequestPasswordReset = async () => {
    try {
      await requestOwnPasswordReset().unwrap();
      toast?.show({
        severity: "success",
        summary: "Password reset sent",
        detail: "Check your email for a secure link to set a new password.",
      });
    } catch (error) {
      toast?.show({
        severity: "error",
        summary: "Request failed",
        detail: getErrorMessage(
          error,
          "Could not send a password reset email."
        ),
      });
    }
  };

  return (
    <PageContainer>
      <Title>My Account</Title>
      <Subtitle>Manage your profile and password.</Subtitle>

      <Card className="block bg-white border-round p-4">
        <SectionTitle>Profile</SectionTitle>
        <form onSubmit={onSaveProfile}>
          <div className="formgrid grid">
            <div className="field col-12">
              <div className="flex align-items-center gap-2">
                <label htmlFor="name">Name</label>
                <CertifiedChip certifiedAt={certifiedAt} />
              </div>
              <Controller
                name="name"
                control={control}
                rules={{
                  required: "Name is required.",
                  validate: (value) =>
                    value.trim().length > 0 || "Name is required.",
                  maxLength: {
                    value: 255,
                    message: "Name must be 255 characters or fewer.",
                  },
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <InputText
                    id="name"
                    className="w-full"
                    autoComplete="name"
                    value={value || ""}
                    onChange={onChange}
                    onBlur={onBlur}
                    aria-labelledby="name"
                  />
                )}
              />
              {errors.name && (
                <small className="p-error">{errors.name.message}</small>
              )}
            </div>

            <div className="field col-12">
              <label htmlFor="email">Email</label>
              <InputText
                id="email"
                className="w-full"
                value={userState.email || ""}
                disabled
              />
              <HelperText>
                To change your email address, please contact an administrator.
              </HelperText>
            </div>
          </div>

          <div className="flex justify-content-end">
            <Button
              type="submit"
              label="Save changes"
              loading={isSavingProfile}
              disabled={!isDirty || !isValid || isSavingProfile}
            />
          </div>
        </form>
      </Card>

      <Card className="block bg-white border-round p-4">
        <SectionTitle>Password</SectionTitle>
        <p>
          We&apos;ll email you a secure link to set a new password. The link is
          sent to your account email address.
        </p>
        <div className="flex justify-content-end">
          <Button
            type="button"
            className="p-button-outlined"
            label="Send password reset email"
            icon="pi pi-envelope"
            loading={isSendingReset}
            disabled={isSendingReset}
            onClick={onRequestPasswordReset}
          />
        </div>
      </Card>
    </PageContainer>
  );
}
